/**
 * הגדרות — the office's own administration home. OA-0001 §3.2.
 *
 *   > *"The GL-POINT operator must NOT be required to decide 'which employee of this customer may see
 *   > Company X?' That decision belongs to the CUSTOMER. GL-POINT supplies the authorization system;
 *   > the customer operates it."*
 *
 * Every entry here is a NOUN and a place (09 §1.3: a noun is a screen, a verb is an action), and the
 * ORDER of the three identity ones is the one thing worth defending in review. They are three
 * different questions with three different tables and three different capabilities, and OA-0001
 * §3.7/§3.8 is that they never collapse into one admin flag:
 *
 *   משתמשים        who exists in the office            `tenant.user.manage` / `tenant.user.invite`
 *   תפקידים        what a user may DO                  `tenant.role.manage`
 *   הרשאות לחברות  which companies they may do it ON   `companies.assignment.manage`
 *
 * D-12 is what happens when the third one is faked: Legacy's admin console wrote
 * `tenant_company.assigned_users`, NO AUTHORIZATION PATH READ IT, and the console reported success
 * while access did not change. Every entry below leads to a screen over a table with a real reader.
 *
 * THE LIST IS NOT A PERMISSION CHECK. A user who may not open one of these screens is refused by
 * the gate, and the refusal arrives as a written Hebrew denial. A hidden entry is presentation; it
 * is never the control.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * IT WAS NINE EQUAL BOXES AND IT IS NOW THREE NAMED SECTIONS. **Owner, 2026-10-25.**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 *     *"אני רוצה שיהיה מסך הגדרות שאני נכנס להגדרות אני רוצה לראות את זה בפריסה כמו באייקונט …
 *     כל מסך ההגדרות הזה לא לטעמי, הוא צריך להיות ברור יותר ובפלואו מסודר"*
 *     *"הפרד בין הגדרות הארגון להגדרות החברה"*
 *
 * Nine same-sized boxes in one undifferentiated grid is a list that makes the reader do the
 * sorting. The reference screen groups by subject and puts one short line under each name, which is
 * what this now does — **הארגון · חברות ולקוחות · אבטחה וחשבון** — and the separation the Owner
 * asked for falls out of the grouping rather than being announced.
 *
 * ── THE NAMES LOST THEIR POSSESSIVES ────────────────────────────────────────────────────────────
 *
 *     *"אל תרשום ארגון שלי, חברה שלי וכו', ארגון שלי לא רלוונטי"*
 *
 * Read as: drop the *"שלי"* labels. The word ארגון stays where it names the thing — an office IS what
 * a tenant is in this product, and *"הגדרות"* alone could not distinguish the office's settings from
 * a company's. What went is *"הארגון שלי"*, *"החברות שלי"*, *"האבטחה שלי"* and *"חברות בארגון"*.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';



/**
 * THE OFFICE'S OWN NAME. **It had a writer and, until this round, no reader.**
 *
 * `tenant.branding.update` was one of the six PUTs G-11 names and the only one of the six that
 * nothing called: this screen DISPLAYED the office's branding and offered no way to change it, so
 * an office could not set its own name. That was fixed; what was NOT fixed, and what the Owner then
 * reported, is that the name it saved was never shown anywhere. `core.tenant_branding` was written
 * by this route and read by nothing in the product, while every screen went on printing
 * `core.tenant.legal_name`. `identityFor` reads the branding row now, which is also what lets this
 * form open with the current name in it rather than blank.
 *
 * ── AND BRANDING IS CONFIGURATION, NEVER AN ACCESS INPUT ───────────────────────────────────────
 *
 * OA-0001 §3.13, and the route says the same: branding is never derived from the logged-in
 * username, never determines company access, and never justifies a second Tenant just to show
 * another logo. That is worth stating ON THE FORM, because "our other office needs its own logo" is
 * exactly how a second tenant gets created and a client list gets split in two.
 *
 * The 409 is a REAL outcome, not an error: two administrators editing at once. It arrives through
 * the ordinary classification, so the person is told the office was changed by somebody else rather
 * than being shown their own text as though it had been saved.
 */
type BrandingOutcome =
  /** `storedAfter` is what the RE-READ returned. `null` = the write answered and the row did not. */
  | {
      readonly kind: 'saved';
      readonly storedAfter: string | null;
      /** `false` means the row is right and the header may not be. A reload fixes it; a re-save does not. */
      readonly displayRefreshed: boolean;
    }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/**
 * A CATEGORY, CARRIED AS TEXT KEYS. **Section 7, 2026-10-26.**
 *
 * It held `titleHe`/`leadHe` -- one language, baked in. A `TextKey` is a reference to BOTH, so the
 * table is language-free and the screen can be read in either without a second table beside it.
 */
/*
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *  THE INDEX THAT USED TO BE HERE, AND WHY IT IS NOT — 2026-10-28
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * This file declared a five-tab table — details, people, companies, billing, security — of which
 * four tabs were lists of LINKS to other screens. The Owner, with a screenshot of this screen:
 * *"לא יכול להיות שהיה לי שתי סרגלים של פרטי הארגון משתמשים ועוד טאבים שלא קשורים להגדרות אלא
 * לחיבורים ותמיכה."*
 *
 * The screens those links reached are the tabs of `AreaBar`, which the shell draws above this
 * content at a fixed position and height. So the table was a second navigation of one area, drawn
 * under the first, with the same two words at the front of both.
 *
 * It is removed rather than reshuffled, and the destinations are NOT lost:
 *
 *   · users, roles, role authoring, companies, security and the subscription — all tabs on the
 *     bar above, each carrying the capability its own route requires (`shell/areas.ts`);
 *   · CONNECTIONS moved OUT of this area entirely, to `CONNECTIONS_AND_SUPPORT`, which is what the
 *     rail already said: a connection to an accounting system is a relationship with another
 *     product, not a setting of the organisation;
 *   · company access lives on the users screen and on a person's own card, where it is granted;
 *   · personal security is a fact about the signed-in ACCOUNT and is declared area-less.
 *
 * What is left is what the screen is called: the organisation's own details.
 */

export function TenantOrganisationScreen(): JSX.Element {
  const ctx = useWorkContext();
  const { t } = useLocale();
  /** `null` = NOT EDITED YET, and the field then shows what is STORED. */
  const [displayNameHe, setDisplayNameHe] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [branding, setBranding] = useState<BrandingOutcome | null>(null);

  /*
   * ── THE FIELD READS WHAT IS STORED, NOT WHAT THE SESSION CARRIES. **Section 1, 2026-10-26.** ──
   *
   * The Owner typed a name, was told it was saved, came back, and the field was empty. The field
   * showed `ctx.tenant?.displayName` -- the SESSION's copy -- so an office whose session did not
   * carry the name saw a blank field under a success message, and the screen could not tell
   * "saved and not reported in this session" from "never saved".
   *
   * `tenant.branding.read` answers what the row says. The two readers are deliberate: the header
   * and the office picker follow the session, this follows the row, and the screen below REPORTS A
   * DISAGREEMENT rather than silently picking one.
   */
  const readBranding = useCallback(() => callOperation('tenant.branding.read', {}), []);
  const stored = useServerData(readBranding);

  async function saveBranding(e: FormEvent, current: string): Promise<void> {
    e.preventDefault();
    setSending(true);
    setBranding(null);
    try {
      // `logoAssetRef` is deliberately NOT sent. There is no asset upload in this product yet, and
      // a field that posted an empty string would clear a mark somebody set elsewhere.
      await callOperation('tenant.branding.update', { body: { displayNameHe: displayNameHe ?? current } });
      /*
       * RE-READ BEFORE CLAIMING ANYTHING, AND READ IT HERE.
       *
       * `useServerData.reload()` returns `void` -- awaiting it proves nothing and the confirmation
       * would quote the value from BEFORE the save. So the route is called directly and the
       * sentence below is written from what IT answered, which is the whole difference between
       * *"we sent it"* and *"it is there"*. `reload()` still runs, to refresh the field.
       */
      const after = await callOperation('tenant.branding.read', {});
      stored.reload();
      /*
       * AND THE DISPLAY CATCHES UP NOW. **Owner, 2026-10-26:** *"לאחר שמירה מוצלחת, עדכן מיד גם
       * את הכותרת ודף הבית, בלי לדרוש יציאה וכניסה. הסבר שההתחברות מחזיקה שם ישן אינו תחליף
       * לעדכון התצוגה."*
       *
       * He is right, and the sentence this replaced was a description of a defect dressed as a
       * feature. The header and the home page name the office from `auth.whoami.read`, so the
       * answer is to re-read it. It is awaited, so the confirmation below appears over a screen
       * that is already correct rather than one that is about to be.
       */
      /*
       * ITS FAILURE IS A DISPLAY PROBLEM, NOT A SAVE PROBLEM, and the two must not share a report.
       * Driving this in a browser produced exactly that: a refresh that threw turned a successful,
       * re-read save into *"no answer from the server"* -- and an operator told that will send the
       * write again.
       */
      let displayRefreshed = true;
      try {
        await ctx.refreshSession();
      } catch {
        displayRefreshed = false;
      }
      setDisplayNameHe(null);
      setBranding({ kind: 'saved', storedAfter: after.displayNameHe, displayRefreshed });
    } catch (err: unknown) {
      setBranding({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  if (ctx.surface !== 'TENANT') {
    return (
      <SurfaceNotice titleHe={t('settings.cat.organisation')} requires="TENANT" actual={ctx.surface} />
    );
  }

  /** What the SESSION says the office is called. Drives the header and the picker. */
  const sessionName = ctx.tenant?.displayName ?? '';
  /**
   * What the ROW says. `null` while the read is in flight, and when no branding row exists.
   *
   * `typeof === 'string'` and NOT `?? null`: a body that omits the field entirely yields
   * `undefined`, and the first version of this line then called `.trim()` on it -- the company-card
   * crash, reproduced in the code written to fix it, and caught by the suite in three cases.
   */
  const storedRaw = stored.result.state === 'loaded' ? stored.result.data.displayNameHe : null;
  const storedName = typeof storedRaw === 'string' && storedRaw.trim() !== '' ? storedRaw : null;
  /*
   * The field prefers the stored name, falls back to the session's, and is empty only when BOTH
   * are -- which is a real state (a brand-new office) and says so below rather than looking broken.
   */
  const officeName = storedName ?? sessionName;
  const nameInForm = displayNameHe ?? officeName;
  /*
   * THERE IS NO "THE HEADER WILL CATCH UP" STATE, AND THERE MUST NOT BE ONE.
   *
   * This screen used to compute `sessionLagging` and tell the Owner the header would show the new
   * name at his next sign-in. He refused that sentence -- *"the explanation that the session holds
   * an old name is no substitute for updating the display"* -- and he was right twice over: it is
   * an excuse, and it is not even true. `identityFor` reads `core.tenant_branding` and PREFERS it
   * over the registered legal name, so a session re-read returns the new name immediately, which
   * is what `saveBranding` now asks for.
   *
   * So the only two outcomes are: the display updated, or the refresh failed and says so. A third
   * state that explains why the old name is still there is a defect wearing a sentence.
   */

  /**
   * THE ORGANISATION FORM, which is now this screen's whole content. It stays a function rather
   * than inline JSX because the two failure arms above it read better with their own statement.
   */
  const renderDetails = (): JSX.Element => (
    <>
      {stored.result.state === 'failed' ? (
        // A REFUSED READ never states that no name is set. Two different facts.
        <ActionFailureNotice failure={stored.result.failure} />
      ) : storedName === null && stored.result.state === 'loaded' ? (
        <p className="settings-office__lag">{t('settings.office.none')}</p>
      ) : null}

      <form className="form" onSubmit={(e) => void saveBranding(e, officeName)}>
        <label className="field">
          <span className="field__label">{t('settings.office.nameLabel')}</span>
          <input
            className="field__input"
            name="displayNameHe"
            value={nameInForm}
            onChange={(e) => setDisplayNameHe(e.currentTarget.value)}
            required
          />
          {/*
            THE HINT IS ONE LINE AND SITS WITH THE FIELD. The Owner asked for *"a short explanation
            beside the action; long detail in collapsible help"* -- so what stays here is what you
            need in order to type, and what left is what explained the product.
          */}
          <span className="field__hint">{t('settings.office.nameHint')}</span>
        </label>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || nameInForm.trim() === '' || nameInForm === officeName}
          >
            {sending ? t('settings.office.saving') : t('settings.office.save')}
          </button>
        </div>
      </form>

      {branding === null ? null : branding.kind === 'saved' ? (
        <StateBlock
          titleHe={t('settings.office.savedTitle')}
          /*
            THE CONFIRMATION IS WRITTEN FROM THE RE-READ, not from what was typed. `saveBranding`
            reloads before it gets here, so this quotes the row -- which is the only version of
            this sentence worth printing.
          */
          bodyHe={
            branding.storedAfter === null
              ? t('settings.office.savedUnreadable')
              : `${t('settings.office.savedTitle')}: “${branding.storedAfter}”`
          }
          nextStep={
            branding.displayRefreshed ? undefined : (
              // SAVED, AND THE HEADER MAY NOT KNOW YET. Said as what it is -- a display that did
              // not refresh -- with the remedy that works. Never "try saving again".
              <p>{t('settings.office.displayStale')}</p>
            )
          }
        />
      ) : (
        <ActionFailureNotice
          failure={branding.failure}
          renderConflict={() => (
            // 409 is two administrators editing at once, and it is a fact rather than a fault.
            // The person must not be left looking at their own text as though it had been saved.
            <StateBlock
              titleHe={t('settings.office.conflictTitle')}
              bodyHe={t('settings.office.conflictBody')}
              tone="error"
              nextStep={<p>{t('settings.office.conflictNext')}</p>}
            />
          )}
        />
      )}
    </>
  );


  return (
    <AppShell
      screenId="tenant-organisation"
      titleHe={t('settings.cat.organisation')}
      /*
       * EVERY STRING ON THIS SCREEN GOES THROUGH `t()`. `translatedScreens.test.ts` fails the
       * build if one Hebrew literal survives here, which is what makes this a claim and not a note.
       */
      translated
      /* BACK TO THE SETTINGS LANDING, which is this screen's parent since 2026-10-28. */
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
      /*
       * NO `provenanceHe`. It carried a true sentence about who decides access in this product,
       * and it was printed on every render of this screen whether or not anybody was asking. That
       * is the general information the Owner moved to the assistant, and this is the screen he
       * named when he did.
       */
    >
      {/*
        ── THE HEADER, FIXED ABOVE THE STRIP ─────────────────────────────────────────────────────
        The subject of the screen, in the shape of the reference card he sent: the name, and under
        it the identity line. It does NOT move into a tab -- a record's identity is not one of its
        topics, and a header that scrolls away is how somebody edits the wrong record.
      */}
      <section className="record-head">
        <h2 className="record-head__name">
          {/* THE ORGANISATION'S OWN NAME IS DATA AND IS NEVER TRANSLATED. Only the fallback is. */}
          {officeName === '' ? t('settings.office.fallbackName') : officeName}
        </h2>
      </section>

      {/*
        ── ONE BAR, AND IT IS THE SHELL'S. **2026-10-28.** ───────────────────────────────────────

        Owner, with a screenshot of this screen: *"לא יכול להיות שהיה לי שתי סרגלים של פרטי הארגון
        משתמשים ועוד טאבים שלא קשורים להגדרות אלא לחיבורים ותמיכה."*

        He is looking at two tab strips, one above the other, both of them navigation and both of
        them carrying פרטי הארגון and משתמשים. The upper one is `AreaBar` — the shell's own bar,
        at the fixed position and height section 1 asked for, listing the screens of this area. The
        lower one was THIS screen's `TabStrip`, four of whose five tabs were groups of LINKS to the
        same screens.

        So the second strip is gone, and nothing it reached is lost: every destination it listed is
        a tab on the bar above, except two that do not belong to this screen and never did —
        company access, which lives on the users screen and on a person's own card where it is
        granted, and personal security, which is a fact about the signed-in ACCOUNT and is declared
        area-less for that reason.

        WHAT IS LEFT IS WHAT THE SCREEN IS CALLED: פרטי הארגון. The header above, and the
        organisation's own details below it. A screen whose tab says *organisation details* and
        whose body is a menu of other screens is the duplication he is pointing at.

        `TabStrip` IS STILL THE RIGHT COMPONENT ONE LEVEL DOWN — inside a user's card and a
        company's card, where the tabs are topics of ONE RECORD rather than a second way to walk
        between screens. That distinction is `AreaBar`'s own header, and this is it being applied.
      */}
      {renderDetails()}
    </AppShell>
  );
}
